import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPaginatedNotifications } from '../Reducers/actions/notificationActions';
import { fetchApi } from '../../helpers/fetch';
import useAdaptivePolling from './useAdaptivePolling';

const useNotificationPolling = () => {
  const dispatch = useDispatch();
  const lastCountRef = useRef(null);
  const currentUnreadCount = useSelector((state) => state.notifications.unreadNotificationsCount);
  const pollingRef = useRef(null);

  // Helper function to compare notification counts
  const hasNotificationCountChanged = (newData, oldCount) => {
    if (oldCount === null) return true; // First time, always update
    if (!newData) return false;
    
    const newCount = newData.unreadTotalCount || 0;
    return newCount !== oldCount;
  };

  const pollForNotifications = async () => {
    // Fetch notification data directly to compare first
    const data = await fetchApi('/api/notifications?limit=1&offset=0');
    
    if (!data.success) {
      throw new Error(`API request failed: ${data.message || 'Unknown error'}`);
    }
    
    const newNotificationData = data.data || {};
    const newCount = newNotificationData.unreadTotalCount || 0;
    const hasChanges = hasNotificationCountChanged(newNotificationData, lastCountRef.current);
    
    if (hasChanges) {
      const oldCount = lastCountRef.current;
      
      // Trigger fast polling if unread count increased (new notifications received)
      if (newCount > oldCount && oldCount !== null) {
        // Trigger fast polling when unread count increases
        if (pollingRef.current?.triggerFastPoll) {
          pollingRef.current.triggerFastPoll();
        }
      }
      
      // Dispatch the action to update Redux store with unread notifications
      const result = await dispatch(getPaginatedNotifications(0, 1, true));
      
      if (result && result.success) {
        lastCountRef.current = newCount;
      }
    }
  };

  // Initialize lastCountRef with current Redux state
  if (currentUnreadCount !== undefined && lastCountRef.current === null) {
    lastCountRef.current = currentUnreadCount;
  }

  // Set up adaptive polling - runs on all pages since header is always visible
  const adaptivePolling = useAdaptivePolling({
    pollingFunction: pollForNotifications,
    enabledRoutes: [], // No route restrictions - header is always present
    context: 'notifications'
  });

  // Store polling reference for triggering fast polling from within pollForNotifications
  pollingRef.current = adaptivePolling;

  // Return utility functions for manual operations
  return {
    ...adaptivePolling,
    pollNow: adaptivePolling.pollNow,
    stopPolling: adaptivePolling.stopPolling
  };
};

export default useNotificationPolling;